import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonCol, IonRow } from '@ionic/react'
import { AccordianComponent } from '../../../../components/accordian/Accordian.components'
import { useTranslation } from 'react-i18next'
import { ITripInformationState } from '../../../tripInformation/store/tripInformation.types'
import { getTripInformationSelector } from '../../../tripInformation/selectors'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import MultiSelect from '../../../../components/multiSelect/MultiSelect.component'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { getMotorVehicleIncidentSelector } from '../selectors'
import { IMotorVehicleIncidentState } from '../store/motorVehicleIncident.types'
import { getCcHpiOptionsSelector } from '../../../options/selectors'
import * as actions from '../store/motorVehicleIncident.actions'
import { IOption } from '../../../../common/common.types'
import ActionButtons from '../../../../components/actionButtons/ActionButtons.component'
import { getDatapointsConfigSelector } from '../../../configurations/selectors'
import { IDataPointsConfig } from '../../../configurations/store/configurations.types'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'

const MotorVehicleIncident: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const motorVehicleIncidentState: IMotorVehicleIncidentState = useSelector(getMotorVehicleIncidentSelector)
  const {
    numberOfVehiclesInvolved,
    airbagDeployed,
    extricationRequired,
    locationOfPatientInVehicle,
    vehicleImpactSite,
  } = motorVehicleIncidentState

  const cchpiOptionsState = useSelector(getCcHpiOptionsSelector)
  const {
    airbagDeployedOptions,
    locationOfPatientInVehicleOptions,
    vehicleImpactSiteOptions,
    extricationRequiredOptions,
  } = cchpiOptionsState

  const dataPointsConfig: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_MVI } = dataPointsConfig

  const tripInformationState: ITripInformationState = useSelector(getTripInformationSelector)
  const { patientCategory } = tripInformationState

  const hasMotorVehicleIncidentData = () => {
    if (
      numberOfVehiclesInvolved ||
      airbagDeployed.length > 0 ||
      extricationRequired ||
      locationOfPatientInVehicle ||
      vehicleImpactSite
    ) {
      return true
    }
    return false
  }

  const setNumberOfVehiclesInvolved = (numbers: string[]) => {
    dispatch(actions.setNumberOfVehiclesInvolved(numbers.join('')))
  }

  const setVehicleImpactSite = (option: IOption) => {
    dispatch(actions.setVehicleImpactSite(option))
  }

  const setLocationOfPatientInVehicle = (option: IOption) => {
    dispatch(actions.setLocationOfPatientInVehicle(option))
  }

  const setExtricationRequired = (option: IOption) => {
    dispatch(actions.setExtricationRequired(option))
  }

  const handleAirBagDeployed = () => {
    const airbagDeployedPositiveVal = [2225, 2230, 2235]
    const airbagDeployedNegativeVal = [2215, 2220]

    let foundPositive = false
    let foundNegativeCtr = 0

    for (const obj of airbagDeployed) {
      const id: any = obj.id

      if (airbagDeployedPositiveVal.includes(id)) {
        foundPositive = true
      }
      if (airbagDeployedNegativeVal.includes(id)) {
        foundNegativeCtr++
      }
    }

    if ((Boolean(foundNegativeCtr) && foundPositive) || foundNegativeCtr > 1) {
      const confirmAirbagDeployedValues: IAlertState = {
        header: '',
        message: t('ccHpi.airbagDeployedValidationMsg'),
        buttons: [{ text: 'Ok' }],
      }
      dispatch(showAlert(confirmAirbagDeployedValues))
      return Boolean(foundNegativeCtr) && foundPositive
    }
  }

  const motorVehicleIncidentContent = (
    <>
      <IonRow>
        <IonCol>
          <NumberPad
            elementId={t('ccHpi.numberOfVehiclesInvolved')}
            dataTestId={t('ccHpi.numberOfVehiclesInvolved')}
            values={numberOfVehiclesInvolved ? numberOfVehiclesInvolved.split('') : []}
            label={t('ccHpi.numberOfVehiclesInvolved')}
            onNumPadClick={setNumberOfVehiclesInvolved}
          />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <MultiSelect
            dataTestID={t('ccHpi.airbagDeployed')}
            label={t('ccHpi.airbagDeployed')}
            listHeader={t('ccHpi.airbagDeployed')}
            options={airbagDeployedOptions}
            savedValues={airbagDeployed}
            action={actions.setAirbagDeployed}
          />
        </IonCol>
      </IonRow>
      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID={t('ccHpi.vehicleImpactSite')}
            options={vehicleImpactSiteOptions}
            selectedOption={vehicleImpactSite}
            listHeader={t('ccHpi.vehicleImpactSite')}
            label={t('ccHpi.vehicleImpactSite')}
            action={setVehicleImpactSite}
          />
          <hr className='field-divider' />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID={t('ccHpi.locationOfPatientInVehicle')}
            options={locationOfPatientInVehicleOptions}
            selectedOption={locationOfPatientInVehicle}
            listHeader={t('ccHpi.locationOfPatientInVehicle')}
            label={t('ccHpi.locationOfPatientInVehicle')}
            action={setLocationOfPatientInVehicle}
          />
          <hr className='field-divider' />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />

      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID={t('ccHpi.extricationRequired')}
            options={extricationRequiredOptions}
            selectedOption={extricationRequired}
            listHeader={t('ccHpi.extricationRequired')}
            label={t('ccHpi.extricationRequired')}
            action={setExtricationRequired}
          />
          <hr className='field-divider' />
        </IonCol>
      </IonRow>

      <hr className='field-divider' />
    </>
  )

  const handleSubmit = () => {
    if (!numberOfVehiclesInvolved && !numberOfVehiclesInvolved.length) {
      const numOfVehicleRequired: IAlertState = {
        header: '',
        message: t('ccHpi.numberOfVehiclesInvolvedValidationMsg'),
        buttons: [{ text: 'Ok' }],
      }
      dispatch(showAlert(numOfVehicleRequired))
      return
    }
    const validateAirbagDeployed = handleAirBagDeployed()
    if (!validateAirbagDeployed) dispatch(actions.saveMotorVehicleIncident())
  }

  const actionButtonObj = [
    {
      label: t('common.buttons.Clear'),
      className: 'btn btn-standard mr-8',
      color: 'danger',
      fill: 'outline',
      onClick: () => {
        dispatch(actions.clearMotorVehicleIncident())
      },
    },
    {
      label: t('common.buttons.Save'),
      className: 'btn btn-standard btn-primary',
      onClick: () => {
        handleSubmit()
      },
    },
  ]

  const footerContent = <ActionButtons actionButtonObj={actionButtonObj} />

  return (
    <>
      {((DP_MVI === 1 && patientCategory?.show_mvi === 1) || hasMotorVehicleIncidentData()) && (
        <IonRow>
          <IonCol className='ion-no-padding'>
            <AccordianComponent
              dataTestId='MotorVehicleIncident'
              heading={t('ccHpi.motorVehicleIncidentHeading')}
              content={motorVehicleIncidentContent}
              isCheckMark={hasMotorVehicleIncidentData()}
              footerContent={footerContent}
            />
            <hr className='field-divider' />
          </IonCol>
        </IonRow>
      )}
    </>
  )
}

export default MotorVehicleIncident
